/**
 * BulkActionBar — fixed toolbar shown while rows are selected.
 * @layer widget
 * @package @plerity/dashboard-shared
 *
 * One bar for every list page. Supports three action styles:
 *   1. inline button (optionally with an icon)
 *   2. dropdown: an action with `options` ("Mark as ▾" -> status list)
 *   3. confirm: an action with `confirm` asks first (destructive deletes)
 *
 * Presentational on purpose. Pair it with `useBulkSelection` from
 * `@plerity/react-shared/hooks` to get "clear the selection + undo toast" for free:
 * call `selection.runBulk({...})` inside an action's `onActivate`.
 *
 * Replaces the byte-identical email + sms copies and the ops CRM list-primitives
 * copy. Props are a superset of the ops variant; the email/sms shape maps as
 *   selectedCount/entityName -> selectedIds + entityName
 *   actions[].onClick        -> actions[].onActivate(ids)
 *   variant "danger"|"default" -> "destructive"|"secondary"
 *   onClearSelection         -> onClearSelection (unchanged)
 *
 * Literal Tailwind ramp with a dark: pair per colour (no CSS vars: the SPA
 * Tailwind v4 build strips them). Dropdown is dependency-free so this widget does
 * not pull react-aria into the eager shared chunk.
 *
 * Shell-aware placement (manager-sms visual QA 2026-10-24, consumers need no change):
 *   - Sidebar: the bar is `position: fixed`, so a bare `inset-x-0` spanned the whole
 *     viewport and covered the bottom of the DashboardLayout sidebar (incl. its
 *     collapse control). While mounted it measures the shell's content column
 *     (`#main-content`) and pins its left/right (and, for `position="top"`, its
 *     top) to that column, tracking sidebar collapse/expand and viewport resizes.
 *     Outside a shell (no `#main-content`) it falls back to full width.
 *   - FAB: while docked at the bottom it writes its own height into the shell's
 *     `--shell-fab-safe-area` var (read by ChatBubble / TesterNotesBubble), so the
 *     bottom-right FAB rides just above the bar instead of covering its right
 *     edge. The previous value is restored on unmount.
 *   - Touch targets: every control is >=44px tall below `sm` (WCAG 2.5.5).
 */

import { ConfirmDialog } from "@plerity/ui-core/ConfirmDialog";
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type BulkActionVariant = "primary" | "secondary" | "destructive";

export interface BulkActionOption {
  value: string;
  label: string;
}

export interface BulkAction<TId extends number | string = number | string> {
  /** Stable key (React key + test hook). */
  key: string;
  label: string;
  icon?: ReactNode;
  /** Default `secondary`. */
  variant?: BulkActionVariant;
  disabled?: boolean;
  /** When true the action is not rendered. */
  hidden?: boolean;
  /** Shows "Processing..." and disables the button while the action is in flight. */
  loading?: boolean;
  /** Ask for confirmation before calling `onActivate` (the dialog's confirm button is always the danger style). */
  confirm?: { title: string; body: string; confirmLabel?: string };
  onActivate: (selectedIds: TId[]) => void | Promise<void>;
  /** Dropdown choices. Renders "Label ▾" and calls `onActivateWithOption` instead of `onActivate`. */
  options?: BulkActionOption[];
  onActivateWithOption?: (selectedIds: TId[], option: string) => void | Promise<void>;
}

export interface BulkActionBarProps<TId extends number | string = number | string> {
  selectedIds: TId[];
  onClearSelection: () => void;
  actions: BulkAction<TId>[];
  /** Default `bottom`. */
  position?: "bottom" | "top";
  /** Full override of the count label. */
  selectionLabel?: (count: number) => string;
  /** Singular noun for the default label: "3 contacts selected". Ignored when `selectionLabel` is set. */
  entityName?: string;
  /** Text of the clear button. Default "Clear selection". */
  clearLabel?: string;
  /** Disables every control while a bulk request is in flight. */
  busy?: boolean;
  /** Rendered as `data-help-context` so the help system can anchor to the bar. */
  helpContext?: string;
  className?: string;
}

/** 44px minimum touch target on phones; the compact desktop height is unchanged. */
const TOUCH_TARGET = "min-h-11 sm:min-h-0";

const BASE_BUTTON = `${TOUCH_TARGET} rounded-lg px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`;

/** The DashboardLayout content column (`<main id="main-content">`). */
const SHELL_MAIN_CONTENT_SELECTOR = "#main-content";

/** CSS var the shell FABs add to their `bottom` offset (see dashboard-shell `shellFab.ts`). */
const SHELL_FAB_SAFE_AREA_VAR = "--shell-fab-safe-area";

interface ColumnInset {
  left: number;
  right: number;
  top: number;
}

/**
 * Edges of the shell content column, measured relative to the viewport, or
 * `null` when there is no laid-out column (non-shell host, jsdom) so the bar
 * keeps its full-width `inset-x-0` fallback.
 */
function measureColumn(): ColumnInset | null {
  const main = document.querySelector<HTMLElement>(SHELL_MAIN_CONTENT_SELECTOR);
  if (!main) return null;
  const rect = main.getBoundingClientRect();
  if (rect.width <= 0) return null;
  const viewport = document.documentElement.clientWidth || window.innerWidth;
  // clientWidth excludes the column's own vertical scrollbar, so the bar never sits on it.
  const innerRight = main.clientWidth > 0 ? rect.left + main.clientWidth : rect.right;
  return {
    left: Math.max(0, Math.round(rect.left)),
    right: Math.max(0, Math.round(viewport - innerRight)),
    top: Math.max(0, Math.round(rect.top)),
  };
}

function useShellColumnInset(active: boolean): ColumnInset | null {
  const [inset, setInset] = useState<ColumnInset | null>(null);
  useLayoutEffect(() => {
    if (!active || typeof document === "undefined") return;
    const update = () => {
      const next = measureColumn();
      setInset((prev) =>
        prev && next && prev.left === next.left && prev.right === next.right && prev.top === next.top ? prev : next
      );
    };
    update();
    const main = document.querySelector<HTMLElement>(SHELL_MAIN_CONTENT_SELECTOR);
    // The column resizes whenever the sidebar collapses/expands (its left edge moves with it).
    const ro = main && typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (main) ro?.observe(main);
    window.addEventListener("resize", update);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [active]);
  return active ? inset : null;
}

/** Lift the shell FAB above the bar while it is docked at the bottom; restore the prior value on unmount. */
function useLiftShellFab(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el) return;
    const root = document.documentElement;
    const previous = root.style.getPropertyValue(SHELL_FAB_SAFE_AREA_VAR);
    const apply = () => root.style.setProperty(SHELL_FAB_SAFE_AREA_VAR, `${Math.ceil(el.offsetHeight)}px`);
    apply();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      if (previous) root.style.setProperty(SHELL_FAB_SAFE_AREA_VAR, previous);
      else root.style.removeProperty(SHELL_FAB_SAFE_AREA_VAR);
    };
  }, [ref, active]);
}

function variantClasses(variant: BulkActionVariant): string {
  switch (variant) {
    case "primary":
      return `${BASE_BUTTON} bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500`;
    case "destructive":
      return `${BASE_BUTTON} bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500`;
    default:
      return `${BASE_BUTTON} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700`;
  }
}

function defaultLabel(count: number, entityName?: string): string {
  if (!entityName) return `${count} selected`;
  return `${count} ${entityName}${count === 1 ? "" : "s"} selected`;
}

interface DropdownActionProps<TId extends number | string> {
  action: BulkAction<TId>;
  selectedIds: TId[];
  disabled: boolean;
  position: "bottom" | "top";
}

function DropdownAction<TId extends number | string>({
  action,
  selectedIds,
  disabled,
  position,
}: DropdownActionProps<TId>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      close(true);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const items = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (items.length === 0) return;
    event.preventDefault();
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = event.key === "ArrowDown" ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: container only forwards menu keyboard events (Escape/arrows) from its menuitems
    <div ref={rootRef} className="relative" onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={disabled}
        className={variantClasses(action.variant ?? "secondary")}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
      >
        {action.icon != null && <span className="mr-1.5 inline-flex">{action.icon}</span>}
        {action.loading ? "Processing..." : action.label} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={action.label}
          className={`absolute right-0 z-50 w-48 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 ${
            position === "top" ? "top-full mt-1" : "bottom-full mb-1"
          }`}
        >
          {(action.options ?? []).map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitem"
              onClick={() => {
                close(true);
                void action.onActivateWithOption?.(selectedIds, option.value);
              }}
              className="block min-h-11 w-full px-3 py-2 text-left sm:min-h-0 text-sm text-gray-700 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:text-gray-200 dark:hover:bg-gray-700 dark:focus-visible:bg-gray-700"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function BulkActionBar<TId extends number | string = number | string>({
  selectedIds,
  onClearSelection,
  actions,
  position = "bottom",
  selectionLabel,
  entityName,
  clearLabel = "Clear selection",
  busy = false,
  helpContext,
  className = "",
}: BulkActionBarProps<TId>) {
  const [pendingConfirm, setPendingConfirm] = useState<BulkAction<TId> | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const count = selectedIds.length;
  const inset = useShellColumnInset(count > 0);
  useLiftShellFab(barRef, count > 0 && position === "bottom");
  if (count === 0) return null;

  const label = selectionLabel ? selectionLabel(count) : defaultLabel(count, entityName);
  const edge = position === "top" ? "top-0 border-b" : "bottom-0 border-t";
  const visible = actions.filter((action) => !action.hidden);
  // Measured runtime geometry (not a design value): pin the bar to the shell content column.
  const placement: CSSProperties | undefined = inset
    ? { left: inset.left, right: inset.right, ...(position === "top" ? { top: inset.top } : {}) }
    : undefined;

  return (
    <>
      <div
        ref={barRef}
        role="toolbar"
        aria-label="Bulk actions"
        data-help-context={helpContext}
        data-placement={inset ? "shell-column" : "viewport"}
        style={placement}
        className={`fixed inset-x-0 ${edge} z-40 border-gray-200 bg-white px-6 py-3 shadow-lg dark:border-gray-700 dark:bg-gray-800 ${className}`}
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
          <span aria-live="polite" className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {label}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {visible.map((action) => {
              const disabled = busy || action.disabled === true || action.loading === true;
              if (action.options && action.options.length > 0) {
                return (
                  <DropdownAction
                    key={action.key}
                    action={action}
                    selectedIds={selectedIds}
                    disabled={disabled}
                    position={position}
                  />
                );
              }
              return (
                <button
                  key={action.key}
                  type="button"
                  disabled={disabled}
                  className={variantClasses(action.variant ?? "secondary")}
                  onClick={() => {
                    if (action.confirm) setPendingConfirm(action);
                    else void action.onActivate(selectedIds);
                  }}
                >
                  {action.icon != null && (
                    <span className="mr-1.5 inline-flex" aria-hidden="true">
                      {action.icon}
                    </span>
                  )}
                  {action.loading ? "Processing..." : action.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={onClearSelection}
              disabled={busy}
              className="min-h-11 rounded-lg px-3 py-1.5 text-sm text-gray-600 sm:min-h-0 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-300 dark:hover:text-white"
            >
              {clearLabel}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={pendingConfirm?.confirm !== undefined}
        title={pendingConfirm?.confirm?.title ?? ""}
        message={pendingConfirm?.confirm?.body}
        confirmLabel={pendingConfirm?.confirm?.confirmLabel ?? "Confirm"}
        variant="danger"
        onCancel={() => setPendingConfirm(null)}
        onConfirm={() => {
          const action = pendingConfirm;
          setPendingConfirm(null);
          if (action) void action.onActivate(selectedIds);
        }}
      />
    </>
  );
}
